import { push } from 'connected-react-router';
import { fetchAccessTokenIfNeeded, invokeWithAccessToken } from '~/AccessToken';
import {
	createCzReimbursementFromSuklCodes,
	createEvidenceExport as apiCreateEvidenceExport,
	createUserProductGroup,
	createUserProductGroupExportXmlUrl,
	ExportErrorResponse,
	ExportResult,
	Filter,
	findUserProductGroup,
	findUserProductGroups,
	removeUserProductGroup,
	updateHistoricDataViewSettings,
	updateUserProductGroup,
	UserProductGroup,
	UserProductGroupErrorResponse,
} from '~/Api/User/ProductGroup';
import { createUserProductGroupFromVirtualPack } from '~/Api/User/ProductGroup/createUserProductGroup';
import { invokeApi } from '~/ApiClient';
import config from '~/config';
import { handleError } from '~/ErrorHandler';
import { flashErrorMessage } from '~/FlashMessage';
import { ThunkAction } from '~/Store';

import { Action, ActionType } from './Action';
import ActionTypes from './ActionTypes';
import buildGroupInitialUrl from './buildGroupInitialUrl';

export function fetchGroups(filter: Filter, page = 1, perPage = 100): ThunkAction<Promise<void>, any> {
	return async (dispatch) => {
		const request = dispatch(
			invokeWithAccessToken((accessToken) => {
				return dispatch(invokeApi(findUserProductGroups, accessToken.user.id, filter, page, perPage));
			})
		);
		dispatch({ type: ActionTypes.requestGroups, request, filter });

		try {
			const response = await request;
			dispatch({
				type: ActionTypes.receiveGroups,
				request,
				filter,
				groups: response.items,
				pagination: response.pagination,
			});
		} catch (error) {
			dispatch(handleError(error));
			dispatch({
				type: ActionTypes.receiveGroups,
				request,
				filter,
				groups: null,
				pagination: null,
			});
		}
	};
}

export function fetchGroup(id: string): ThunkAction<Promise<UserProductGroup | null>, any> {
	return async (dispatch) => {
		const request = dispatch(
			invokeWithAccessToken((accessToken) => {
				return dispatch(invokeApi(findUserProductGroup, accessToken.user.id, id));
			})
		);
		dispatch({ type: ActionTypes.requestGroup, request, id });

		try {
			const response = await request;
			dispatch({
				type: ActionTypes.receiveGroup,
				request,
				id,
				group: response.productGroup,
				notFound: false,
			});
			return response.productGroup;
		} catch (error) {
			const notFound = error instanceof UserProductGroupErrorResponse && error.handleProductGroupNotFound();
			dispatch({
				type: ActionTypes.receiveGroup,
				request,
				id,
				group: null,
				notFound,
			});
			dispatch(handleError(error));
		}
		return null;
	};
}

export function paginatedFetchGroups(
	filter: Filter = new Filter(null, null),
	perPage = 100
): ThunkAction<Promise<void>, Action> {
	return async (dispatch, getState) => {
		const groupsState = getState().productGroup.byFilter[filter.key];
		if (!groupsState || !groupsState.pagination) {
			await dispatch(fetchGroups(filter, 1, perPage));
			return;
		}

		const nextPage = groupsState.pagination.getNextPage();
		if (nextPage) {
			await dispatch(fetchGroups(filter, nextPage, perPage));
		}
	};
}

export function fetchRecentGroups(): ThunkAction<Promise<void>, any> {
	return async (dispatch, getState) => {
		const { recentStorage } = getState().productGroup;
		const request = dispatch(
			invokeWithAccessToken((accessToken) => {
				return Promise.all(
					recentStorage.loadIdsBy(accessToken.user.id).map(async (id) => {
						try {
							return await dispatch(invokeApi(findUserProductGroup, accessToken.user.id, id));
						} catch (error) {
							if (error instanceof UserProductGroupErrorResponse && error.handleProductGroupNotFound()) {
								recentStorage.removeGroupId(accessToken.user.id, id);
								return null;
							}
							throw error;
						}
					})
				);
			})
		);
		dispatch({ type: ActionTypes.requestRecent, request });

		try {
			const groups = (await request).filter((response) => response !== null).map((response) => response?.productGroup);
			dispatch({ type: ActionTypes.receiveRecent, request, groups });
		} catch (error) {
			dispatch({ type: ActionTypes.receiveRecent, request, groups: [] });
			dispatch(handleError(error));
		}
	};
}

export function pruneGroups() {
	return { type: ActionTypes.prune };
}

export function createGroup(
	name: string,
	type: string,
	module: string | null = null,
	parentId: string | null = null,
	templateId: string | null = null,
	czReimbursementLists: Array<{ codes: string[] }> | null = null
): ThunkAction<Promise<{ productGroup: UserProductGroup }>, any> {
	return async (dispatch, getState) => {
		const request = dispatch(
			invokeWithAccessToken((accessToken) => {
				return czReimbursementLists === null
					? dispatch(invokeApi(createUserProductGroup, accessToken.user.id, name, type, module, parentId, templateId))
					: dispatch(invokeApi(createCzReimbursementFromSuklCodes, accessToken.user.id, name, czReimbursementLists));
			})
		);
		dispatch({ type: ActionTypes.requestCreate, request });

		try {
			const response = await request;
			const { productGroup } = response;

			dispatch(
				invokeWithAccessToken((accessToken) => {
					const { recentStorage } = getState().productGroup;
					if (productGroup.parent !== null) {
						recentStorage.addGroupId(accessToken.user.id, productGroup.parent.id);
					} else if (productGroup.isGroup()) {
						recentStorage.addGroupId(accessToken.user.id, productGroup.id);
					}
				})
			);

			dispatch({
				type: ActionTypes.receiveCreate,
				request,
				group: productGroup,
			});
			return response;
		} catch (error) {
			dispatch({ type: ActionTypes.receiveCreate, request, group: null });
			throw error;
		}
	};
}

export function createGroupFromVirtualPack(
	name: string,
	parentId: string | null = null,
	virtualPackId: string,
	czReimbursementLists: Array<{ codes: string[] }> | null
): ThunkAction<Promise<{ productGroup: UserProductGroup }>, any> {
	return async (dispatch, getState) => {
		const request = dispatch(
			invokeWithAccessToken((accessToken) => {
				return czReimbursementLists === null
					? dispatch(
							invokeApi(createUserProductGroupFromVirtualPack, accessToken.user.id, virtualPackId, name, parentId)
					  )
					: dispatch(invokeApi(createCzReimbursementFromSuklCodes, accessToken.user.id, name, czReimbursementLists));
			})
		);
		dispatch({ type: ActionTypes.requestCreate, request });

		try {
			const response = await request;
			const { productGroup } = response;

			dispatch(
				invokeWithAccessToken((accessToken) => {
					const { recentStorage } = getState().productGroup;
					if (productGroup.parent !== null) {
						recentStorage.addGroupId(accessToken.user.id, productGroup.parent.id);
					} else if (productGroup.isGroup()) {
						recentStorage.addGroupId(accessToken.user.id, productGroup.id);
					}
				})
			);

			dispatch({
				type: ActionTypes.receiveCreate,
				request,
				group: productGroup,
			});

			return response;
		} catch (error) {
			dispatch({ type: ActionTypes.receiveCreate, request, group: null });
			throw error;
		}
	};
}

export function updateGroup(id: string, name: string): ThunkAction<Promise<{ productGroup: UserProductGroup }>, any> {
	return async (dispatch) => {
		const request = dispatch(
			invokeWithAccessToken((accessToken) => {
				return dispatch(invokeApi(updateUserProductGroup, accessToken.user.id, id, name));
			})
		);
		dispatch({ type: ActionTypes.requestUpdate, request, id });

		try {
			const response = await request;
			dispatch({
				type: ActionTypes.receiveUpdate,
				request,
				id,
				group: response.productGroup,
			});
			return response;
		} catch (error) {
			dispatch({ type: ActionTypes.receiveUpdate, request, id, group: null });
			throw error;
		}
	};
}

export function removeGroup(id: string): ThunkAction<Promise<void>, any> {
	return async (dispatch, getState) => {
		const request = dispatch(
			invokeWithAccessToken((accessToken) => {
				return dispatch(invokeApi(removeUserProductGroup, accessToken.user.id, id));
			})
		);
		dispatch({ type: ActionTypes.requestRemove, request });

		try {
			const response = await request;
			dispatch(
				invokeWithAccessToken((accessToken) => {
					const { recentStorage } = getState().productGroup;
					recentStorage.removeGroupId(accessToken.user.id, id);
				})
			);
			dispatch({ type: ActionTypes.receiveRemove, request, id });
			return response;
		} catch (error) {
			dispatch({ type: ActionTypes.receiveRemove, request, id: null });
			throw error;
		}
	};
}

export function createGroupExportXmlUrl(
	id: string,
	currency: string | null
): ThunkAction<Promise<ExportResult | null>, Action> {
	return async (dispatch) => {
		const request = dispatch(
			invokeWithAccessToken((accessToken) => {
				return dispatch(invokeApi(createUserProductGroupExportXmlUrl, accessToken.user.id, id, currency));
			})
		);
		dispatch({ type: ActionType.requestExport, request });

		try {
			const response = await request;
			dispatch({ type: ActionType.receiveExport, request });
			return response;
		} catch (error) {
			if (error instanceof ExportErrorResponse && error.handleGroupRequiresAtLeastOneProduct()) {
				dispatch(flashErrorMessage('Cannot export group without products'));
			}
			if (error instanceof ExportErrorResponse && error.handlepGroupExportItemsLimit()) {
				const limitError = error.findExportItemsLimitError();
				dispatch(
					flashErrorMessage(
						`Due to technical limitations it's not currently possible to export group with more than ${
							limitError?.limit || '~5000'
						} products. ` + `Please split group to smaller ones or contact us at ${config.support.email}`
					)
				);
			}
			dispatch({ type: ActionType.receiveExport, request });
			dispatch(handleError(error));
		}

		return null;
	};
}

export function createEvidenceExport(id: string): ThunkAction<Promise<ExportResult | null>, Action> {
	return async (dispatch) => {
		const request = dispatch(
			invokeWithAccessToken((accessToken) => {
				return dispatch(invokeApi(apiCreateEvidenceExport, accessToken.user.id, id));
			})
		);
		dispatch({ type: ActionType.requestExport, request });

		try {
			const response = await request;
			dispatch({ type: ActionType.receiveExport, request });
			return response;
		} catch (error) {
			dispatch({ type: ActionType.receiveExport, request });
			dispatch(handleError(error));
		}

		return null;
	};
}

export function updateGroupHistoricDataViewSettings(id: string, date: Date | null): ThunkAction<Promise<void>, Action> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(updateHistoricDataViewSettings, userId, id, date));
		dispatch({
			type: ActionType.requestHistoricDataViewSettingsUpdate,
			request,
			id,
		});

		try {
			const response = await request;
			dispatch({
				type: ActionType.receiveHistoricDataViewSettingsUpdate,
				request,
				id,
				historicDataViewSettings: response.historicDataViewSettings,
			});
		} catch (error) {
			dispatch({
				type: ActionType.receiveHistoricDataViewSettingsUpdate,
				request,
				id,
				historicDataViewSettings: null,
			});
			dispatch(handleError(error));
			throw error;
		}
	};
}

export function redirectFromDeletedGroup(group: UserProductGroup) {
	return push({
		pathname: `/product-groups/${group.parent ? encodeURIComponent(group.parent.id) : ''}`,
	});
}

export function redirectToGroup(group: UserProductGroup): ThunkAction<void, any> {
	return (dispatch, getState) => {
		dispatch(push(buildGroupInitialUrl(group, getState())));
	};
}
